import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';
import { formatDate } from './dateHelpers';
import SLBLogo from '../images/slb-logo-png.png'; // Replace with the actual image file path

export const exportChart = async (
  element: HTMLElement | null,
  type: 'JPG' | 'PNG' | 'SVG' | 'PDF' | 'PRINT',
  filename: string,
  startDate?: Date,
  endDate?: Date,
  pageHeaderContent = 'ESP Chart',
) => {
  if (!element) return;

  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#131f2e',
      scale: 2,
      width: element.scrollWidth + 20,
      onclone: (clonedDoc) => {
        const clonedRecentTests = clonedDoc.getElementById('recent-tests-content');
        if (!clonedRecentTests) return;

        clonedRecentTests.querySelectorAll<HTMLElement>('.legend-text').forEach((el) => {
          el.style.position = 'relative';
          el.style.top = '-7px';
        });
      },
    });

    let dataUrl: string;
    const jpegQuality = type === 'PDF' ? 0.7 : 0.9;

    if (type === 'PNG') {
      dataUrl = canvas.toDataURL('image/png');
    } else {
      dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
    }

    if (type === 'PDF') {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Header bar
      pdf.setFillColor('#01485e');
      pdf.rect(0, 0, pageWidth, 50, 'F');

      pdf.addImage(SLBLogo, 'PNG', 30, 15, 44, 24);

      // Right side label in header
      pdf.setFontSize(12);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`OptiLift`, pageWidth - 70, 30, { align: 'right' });

      // Title below header
      pdf.setFontSize(20);
      pdf.setTextColor(0, 0, 0);
      pdf.text(pageHeaderContent, pageWidth / 2, 80, { align: 'center' });

      // Add selected date range
      pdf.setFontSize(12);
      const dateRangeText =
        startDate && endDate
          ? `${moment(startDate).format('MMM D, YYYY')} – ${moment(endDate).format('MMM D, YYYY')}`
          : '';
      pdf.text(dateRangeText, pageWidth / 2, 100, { align: 'center' });

      // Resize image to fit page with margins
      const maxImageWidth = pageWidth - 60;
      const maxImageHeight = pageHeight - 200;
      const imgAspectRatio = canvas.width / canvas.height;

      let imgWidth = maxImageWidth;
      let imgHeight = imgWidth / imgAspectRatio;

      if (imgHeight > maxImageHeight) {
        imgHeight = maxImageHeight;
        imgWidth = imgHeight * imgAspectRatio;
      }

      const x = (pageWidth - imgWidth) / 2;
      const y = 120;

      const pdfImageFormat = dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      pdf.addImage(dataUrl, pdfImageFormat, x, y, imgWidth, imgHeight);

      // Footer
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      const str = formatDate(new Date(), true);
      pdf.text(`Generated By: Auto Export`, 30, pageHeight - 30);
      pdf.text(`Generated on: ${str}`, pageWidth - 30, pageHeight - 30, { align: 'right' });

      pdf.save(`${filename}.pdf`);
      return;
    }

    // Default: image download (JPG or PNG)
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${filename}.${type.toLowerCase()}`;
    link.click();
  } catch (error) {
    console.error(`Error exporting chart as ${type}:`, error);
  }
};

export type ExportType = 'PRINT' | 'JPG' | 'PNG' | 'SVG' | 'PDF';

function getExportType(option: string): ExportType {
  switch (option) {
    case 'Print Chart':
      return 'PDF';
    case 'Download as JPG':
      return 'JPG';
    case 'Download as PNG':
      return 'PNG';
    case 'Download as SVG':
      return 'SVG';
    case 'Download as PDF':
      return 'PDF';
    default:
      return 'PNG';
  }
}
export async function downloadChartUtil(
  option: string,
  finalFilename: string,
  element: HTMLElement | null,
  pageHeaderContent?: string,
) {
  if (!element) return;

  const fullscreenButton = element.querySelector('.chart-action-button') as HTMLElement;
  const downloadMenu = element.querySelector('.trend-chart-controls') as HTMLElement;

  if (fullscreenButton) fullscreenButton.style.display = 'none';
  if (downloadMenu) downloadMenu.style.display = 'none';

  await exportChart(element, getExportType(option), finalFilename, undefined, undefined, pageHeaderContent);

  if (fullscreenButton) fullscreenButton.style.display = '';
  if (downloadMenu) downloadMenu.style.display = '';
}
export async function handleChartDownloadUtil(
  option: string,
  index: number,
  fullscreenChartIndex: number | null,
  filename?: string,
  pageHeaderContent?: string,
) {
  let element: HTMLElement | null;

  if (fullscreenChartIndex !== null && fullscreenChartIndex === index) {
    element = document.getElementById('fullscreen-chart-container');
  } else {
    element = document.getElementById(`chart-wrapper-${index}`);
  }

  const finalFilename = filename || `chart-${index + 1}`;

  if (!element) return;

  const fullscreenButton = element.querySelector('.chart-action-button') as HTMLElement;
  const downloadMenu = element.querySelector('.trend-chart-controls') as HTMLElement;

  if (fullscreenButton) fullscreenButton.style.display = 'none';
  if (downloadMenu) downloadMenu.style.display = 'none';

  await exportChart(element, getExportType(option), finalFilename, undefined, undefined, pageHeaderContent);

  if (fullscreenButton) fullscreenButton.style.display = '';
  if (downloadMenu) downloadMenu.style.display = '';
}
